import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";
import { brand_orange, brand_dark } from "../../constants/styles";
import PagedCarousel from "../Carousel";
import SectionShell, { SectionTitle, SectionSubtitle } from "../SectionShell";
import usePagedSection from "../usePagedSection";
import CardActionsOverlay from "../CardActionsOverlay";
import { Skeleton } from "../Skeleton";
import { apiGet } from "../../api";

const Card = styled.article`
  background: #f9fafb;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  }

  &[data-theme="dark"], [data-theme="dark"] & {
    background: #17171e;

    &:hover {
      background: #1f2937;
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
    }
  }
`;

const Cover = styled.div`
  height: 160px;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  position: relative;
  overflow: hidden;
  outline: none;
`;

const Avatar = styled.div`
  position: absolute;
  bottom: -18px;
  left: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e5e7eb;
  border: 2px solid #fff;
  overflow: hidden;

  [data-theme="dark"] & {
    background: #374151;
    border-color: #17171e;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Body = styled.div`
  padding: 28px 16px 20px;
`;

const Handle = styled.div`
  font-weight: 700;
  font-size: 13px;
  color: #1f2937;
  margin-bottom: 8px;

  &[data-theme="dark"], [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const Preview = styled.p`
  font-size: 12px;
  color: #374151;
  line-height: 1.6;
  margin: 0 0 16px;
  min-height: 4em;

  &[data-theme="dark"], [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const LearnMore = styled.button`
  background: ${brand_orange};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 700;
  cursor: pointer;
  font-size: 12px;
  transition: filter 150ms ease;
  display: inline-block;
  text-decoration: none;

  &:hover { filter: brightness(0.95); }
`;

const ExploreWrap = styled.div`
  text-align: center;
  margin-top: 48px;
`;

// Inertia-powered navigation: Link replaces a full page reload with an
// XHR that swaps the SPA component, so clicking "Explore" feels instant
// instead of blanking the viewport. Inertia falls back to a full reload
// automatically if the server responds with a non-Inertia page.
const Explore = styled(Link)`
  background: ${brand_dark};
  color: #fff;
  border-radius: 999px;
  padding: 12px 48px;
  text-decoration: none;
  font-weight: 600;
  display: inline-block;
  transition: background 150ms ease, transform 100ms ease;

  &:hover { background: #111827; transform: translateY(-1px); color: #fff; }

  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 3px;
  }
`;

function renderThreadSkeleton(i) {
  return (
    <Card key={`sk-${i}`} aria-hidden="true">
      <Cover>
        <Avatar />
      </Cover>
      <Body>
        <Skeleton $width="45%" $height="14px" $radius="4px" />
        <div style={{ height: 12 }} />
        <Skeleton $width="95%" $height="12px" $radius="4px" />
        <div style={{ height: 4 }} />
        <Skeleton $width="90%" $height="12px" $radius="4px" />
        <div style={{ height: 4 }} />
        <Skeleton $width="60%" $height="12px" $radius="4px" />
        <div style={{ height: 16 }} />
        <Skeleton $width="110px" $height="30px" $radius="8px" />
      </Body>
    </Card>
  );
}

function renderThreadCard(t) {
  return (
    <Card key={t.id}>
      <Cover data-card-host tabIndex={0}>
        <Avatar aria-hidden="true">
          {t.author_avatar && (
            <AvatarImage src={t.author_avatar} alt="" loading="lazy" />
          )}
        </Avatar>
        <CardActionsOverlay
          detailLink={t.link}
          showLike={false}
          showDislike={false}
          showBookmark={false}
          showDetail={!!t.link}
          radius="16px 16px 0 0"
        />
      </Cover>
      <Body>
        <Handle>{t.author_handle}</Handle>
        <Preview>{t.content}</Preview>
        {t.link ? (
          <LearnMore as="a" href={t.link}>{t.cta_label}</LearnMore>
        ) : (
          <LearnMore type="button">{t.cta_label}</LearnMore>
        )}
      </Body>
    </Card>
  );
}

function TaxsamCommunity() {
  // The community endpoint is non-paginated and returns up to 8 items. We
  // fetch once on mount, then feed the fixed slice through usePagedSection's
  // `sampleItems` path (which handles the client-side slicing and the
  // narrow ↔ wide perPage responsive behavior).
  const [threads, setThreads] = React.useState([]);
  // Tracks the in-flight initial fetch separately from the hook's loading.
  // Without this, usePagedSection resolves the empty sampleItems array
  // synchronously and would briefly render the empty-state message before
  // the real threads arrive.
  const [fetching, setFetching] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    apiGet('/api/v1/homepage/community_threads')
      .then(({ parsedResponse }) => {
        if (cancelled) return;
        setThreads(Array.isArray(parsedResponse?.data) ? parsedResponse.data : []);
      })
      .catch((err) => {
        if (typeof console !== 'undefined') {
          console.warn('[TaxsamCommunity] fetch failed', err);
        }
        if (!cancelled) setThreads([]);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => { cancelled = true; };
  }, []);

  const { items, pageIdx, setPageIdx, totalPages, loading, perPage } =
    usePagedSection({ sampleItems: threads });

  return (
    <SectionShell id="community">
      <SectionTitle>Taxsam.Community</SectionTitle>
      <SectionSubtitle>
        Jangan Sampai Terlewatkan Kesempatan Untuk Konsultasi Dan Diskusi Interaktif Bersama Mentor-Mentor Praktisi Dan Akademisi Berpengalaman Kami
      </SectionSubtitle>
      <PagedCarousel
        items={items}
        perPage={perPage}
        pageIdx={pageIdx}
        totalPages={totalPages}
        onPageChange={setPageIdx}
        loading={fetching || loading}
        renderItem={renderThreadCard}
        renderSkeleton={renderThreadSkeleton}
        ariaLabel="Community threads"
        emptyMessage="Belum ada diskusi komunitas."
      />
      <ExploreWrap>
        <Explore href="/communities">Explore</Explore>
      </ExploreWrap>
    </SectionShell>
  );
}

export default React.memo(TaxsamCommunity);
